<template>
  <header class="site-header">
    <!-- Top Bar -->
    <div class="top-bar">
      <div class="container">
        <div class="contact-info">
          <span class="email">
            <svg class="icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <polyline points="22,6 12,13 2,6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <a href="mailto:info@discoverchinatoday.com">info@discoverchinatoday.com</a>
          </span>
        </div>
        <div class="search">
          <SearchBox />
        </div>
      </div>
    </div>
    <!-- Navigation Bar -->
    <nav class="main-nav">
      <div class="container nav-container">
        <!-- Logo -->
        <div class="logo">
          <router-link to="/" class="logo-link">
            <img :src="logoImage" alt="Discover China Today" />
            <span class="logo-text">Discover China Today</span>
          </router-link>
        </div>
        
        <!-- Mobile Menu Button -->
        <button 
          class="mobile-menu-btn" 
          @click="toggleMobileMenu"
          :class="{ active: mobileMenuOpen }"
          aria-label="Toggle menu"
        >
          <span class="hamburger-line"></span>
          <span class="hamburger-line"></span>
          <span class="hamburger-line"></span>
        </button>
        
        <!-- Navigation Menu -->
        <div class="nav-menu" :class="{ open: mobileMenuOpen }">
          <ul class="nav-links">
            <li class="nav-item">
              <router-link 
                to="/explore" 
                class="nav-link"
                @click="closeMobileMenu"
              >
                <span>Explore</span>
              </router-link>
            </li>
            <li class="nav-item">
              <router-link 
                to="/culture" 
                class="nav-link"
                @click="closeMobileMenu"
              >
                <span>Culture</span>
              </router-link>
            </li>
            <li class="nav-item">
              <router-link 
                to="/stories" 
                class="nav-link"
                @click="closeMobileMenu"
              >
                <span>Stories</span>
              </router-link>
            </li>
            <li class="nav-item legacy-nav" :class="{ open: legacyMenuOpen }">
              <button 
                class="nav-link legacy-toggle" 
                type="button" 
                @click.stop="toggleLegacyMenu"
                aria-haspopup="true"
                :aria-expanded="legacyMenuOpen.toString()"
              >
                <span>Legacy Links</span>
                <svg class="chevron" viewBox="0 0 24 24">
                  <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
              <div class="legacy-dropdown" v-if="legacyMenuOpen">
                <p class="legacy-label">Original DiscoverChinaToday pages</p>
                <ul>
                  <li v-for="link in legacyLinks" :key="link.path">
                    <a :href="link.path" @click="closeMenus">{{ link.label }}</a>
                  </li>
                </ul>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  </header>
</template>

<script>
import SearchBox from './SearchBox.vue';
import logoImage from '@/assets/images/logo.png';
import legacyLinks from '@/data/legacyLinks';

export default {
  name: 'Header',
  components: {
    SearchBox,
  },
  data() {
    return {
      logoImage,
      mobileMenuOpen: false,
      legacyMenuOpen: false,
      legacyLinks,
      documentClickHandler: null,
      resizeHandler: null,
    };
  },
  methods: {
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
    },
    closeMobileMenu() {
      this.mobileMenuOpen = false;
      this.closeLegacyMenu();
    },
    toggleLegacyMenu() {
      this.legacyMenuOpen = !this.legacyMenuOpen;
    },
    closeLegacyMenu() {
      this.legacyMenuOpen = false;
    },
    closeMenus() {
      this.closeMobileMenu();
      this.closeLegacyMenu();
    },
  },
  mounted() {
    this.documentClickHandler = (e) => {
      if (!this.$el.contains(e.target)) {
        this.closeMenus();
      }
    };
    document.addEventListener('click', this.documentClickHandler);

    this.resizeHandler = () => {
      if (window.innerWidth > 768) {
        this.closeMenus();
      }
    };
    window.addEventListener('resize', this.resizeHandler);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.documentClickHandler);
    window.removeEventListener('resize', this.resizeHandler);
  },
};
</script>

<style scoped>
.site-header {
  position: sticky;
  top: 0;
  z-index: 100;
  background: var(--neutral-100);
  box-shadow: var(--shadow-sm);
}

/* Top Bar Styles */
.top-bar {
  background-color: var(--neutral-800);
  color: var(--neutral-300);
  padding: var(--space-2) 0;
  font-size: var(--text-sm);
}

.top-bar .container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.contact-info {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.icon {
  width: 16px;
  height: 16px;
}

.email a {
  color: var(--neutral-300);
  transition: color var(--transition-fast);
}

.email a:hover {
  color: var(--neutral-100);
}

/* Main Navigation */
.main-nav {
  background: var(--neutral-100);
  border-bottom: 1px solid var(--neutral-300);
  backdrop-filter: blur(10px);
}

.nav-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-4) var(--space-4);
  position: relative;
}

/* Logo Styles */
.logo {
  z-index: 102;
}

.logo-link {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  text-decoration: none;
  transition: transform var(--transition-fast);
}

.logo-link:hover {
  transform: scale(1.02);
}

.logo img {
  height: 40px;
  width: auto;
}

.legacy-nav {
  position: relative;
}

.legacy-toggle {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  background: transparent;
  border: none;
  color: inherit;
  font: inherit;
  cursor: pointer;
  padding: 0;
}

.legacy-toggle .chevron {
  width: 16px;
  height: 16px;
  transition: transform var(--transition-fast);
}

.legacy-nav.open .chevron {
  transform: rotate(180deg);
}

.legacy-dropdown {
  position: absolute;
  top: calc(100% + var(--space-3));
  right: 0;
  width: 320px;
  background: var(--neutral-0);
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--neutral-200);
  border-radius: var(--radius-lg);
  padding: var(--space-4);
  z-index: 105;
}

.legacy-dropdown ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.legacy-dropdown a {
  color: var(--neutral-800);
  text-decoration: none;
  font-size: var(--text-sm);
  display: block;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  transition: background-color var(--transition-fast);
}

.legacy-dropdown a:hover {
  background-color: var(--neutral-100);
}

.legacy-label {
  font-size: var(--text-xs);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--neutral-500);
  margin-bottom: var(--space-3);
}

@media (max-width: 768px) {
  .legacy-dropdown {
    position: static;
    width: 100%;
    margin-top: var(--space-3);
    box-shadow: none;
  }
}

.logo-text {
  font-family: var(--font-display);
  font-size: var(--text-2xl);
  font-weight: 700;
  color: var(--primary-color);
}

/* Mobile Menu Button */
.mobile-menu-btn {
  display: none;
  flex-direction: column;
  justify-content: space-around;
  width: 24px;
  height: 24px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 102;
}

.hamburger-line {
  width: 100%;
  height: 2px;
  background: var(--neutral-700);
  transition: all var(--transition-normal);
  transform-origin: center;
}

.mobile-menu-btn.active .hamburger-line:nth-child(1) {
  transform: rotate(45deg) translate(6px, 6px);
}

.mobile-menu-btn.active .hamburger-line:nth-child(2) {
  opacity: 0;
}

.mobile-menu-btn.active .hamburger-line:nth-child(3) {
  transform: rotate(-45deg) translate(6px, -6px);
}

/* Navigation Menu */
.nav-menu {
  display: flex;
  align-items: center;
}

.nav-links {
  display: flex;
  align-items: center;
  gap: var(--space-8);
  list-style: none;
  margin: 0;
  padding: 0;
}

.nav-item {
  position: relative;
}

.nav-link {
  display: flex;
  align-items: center;
  padding: var(--space-2) var(--space-4);
  font-weight: 500;
  color: var(--neutral-700);
  text-decoration: none;
  border-radius: var(--radius-lg);
  transition: all var(--transition-fast);
  position: relative;
}

.nav-link:hover {
  color: var(--primary-color);
  background-color: var(--neutral-200);
}

.nav-link.router-link-active {
  color: var(--primary-color);
  font-weight: 600;
}

.nav-link.router-link-active::after {
  content: '';
  position: absolute;
  bottom: -12px;
  left: 50%;
  transform: translateX(-50%);
  width: 6px;
  height: 6px;
  background: var(--primary-color);
  border-radius: var(--radius-full);
}

/* Mobile Styles */
@media (max-width: 768px) {
  .top-bar {
    padding: var(--space-1) 0;
  }
  
  .top-bar .container {
    flex-direction: column;
    gap: var(--space-2);
  }

  .mobile-menu-btn {
    display: flex;
  }

  .nav-menu {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    background: var(--neutral-100);
    flex-direction: column;
    justify-content: center;
    align-items: center;
    transform: translateX(-100%);
    transition: transform var(--transition-normal);
    z-index: 101;
  }

  .nav-menu.open {
    transform: translateX(0);
  }

  .nav-links {
    flex-direction: column;
    gap: var(--space-6);
    text-align: center;
  }

  .nav-link {
    font-size: var(--text-xl);
    padding: var(--space-4) var(--space-6);
  }

  .nav-link.router-link-active::after {
    display: none;
  }
}

@media (max-width: 480px) {
  .logo-text {
    font-size: var(--text-lg);
  }
  
  .nav-container {
    padding: var(--space-3) var(--space-4);
  }
}
</style>
